import { FormEvent, useState } from "react";
import "./FeedbackPage.css";
import ReCAPTCHA from 'react-google-recaptcha';
import config from "../../config";

type FeedbackFormProps = {
  onCancel?: () => void;
  onSent?: () => void;
};

function FeedbackForm({ onCancel, onSent }: FeedbackFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSending(true);
    setStatus("");

    if (!recaptchaSiteKey) {
      setStatus("Feedback is temporarily unavailable. Please try again later.");
      setIsSending(false);
      return;
    }

    // Check if captcha is provided
    if (!captchaValue) {
      setStatus("Please complete the captcha");
      setIsSending(false);
      return;
    }

    try {
      const apiOrigin = config.apiBaseUrl.replace(/\/api\/v1\/?$/, "").replace(/\/$/, "");
      const feedbackUrl = `${apiOrigin}/api/feedback`;
      const response = await fetch(feedbackUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, captcha: captchaValue }), // Include captcha in body
      });
      const result = await response.text();

      if (!response.ok || result !== "Feedback received") {
        setStatus(result || "Error sending feedback");
        return;
      }

      setStatus("Feedback sent successfully");
      setName("");
      setEmail("");
      setMessage("");
      onSent?.();
    } catch {
      setStatus("Error sending feedback");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <form className="feedback-form" onSubmit={handleSubmit}>
      <label htmlFor="feedback-name">Name</label>
      <input
        type="text"
        id="feedback-name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
      />

      <label htmlFor="feedback-email">Email</label>
      <input
        type="email"
        id="feedback-email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        required
      />

      <label htmlFor="feedback-message">Feedback</label>
      <textarea
        id="feedback-message"
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        required
        rows={5}
      />

      <label>Captcha</label>
      {recaptchaSiteKey ? (
        <ReCAPTCHA
          sitekey={recaptchaSiteKey}
          onChange={setCaptchaValue}
        />
      ) : (
        <p className="feedback-status" role="alert">
          Feedback is temporarily unavailable. Please try again later.
        </p>
      )}

      {status && <p className="feedback-status" role="status">{status}</p>}

      <div className="feedback-form-actions">
        {onCancel && (
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={isSending}>
          {isSending ? "Sending..." : "Send"}
        </button>
      </div>
    </form>
  );
}

export default FeedbackForm;